import logger from '../utils/logger';
import { getFullUrl } from '../config/apiConfig';
import { userPool } from './cognitoService';

// Get current JWT token from backend via httpOnly cookie session
const getAuthToken = async () => {
   const newToken = await refreshAuthToken();  
  try {
    
    const url = getFullUrl('auth/session');
    const payload = JSON.stringify({
      userPool
    });
    
    

    const response = await fetch(url, {
      method: 'POST', // Use POST since GET endpoint doesn't exist
      credentials: 'include', // Send httpOnly cookies
      headers: {
        'Content-Type': 'application/json'
      },
      body: payload
    });

    // Log all response details for debugging
    
    response.headers.forEach((value, name) => {
   });
    
    // Specifically check for Set-Cookie header
    const setCookieHeader = response.headers.get('Set-Cookie');
     
    // Check all headers for any cookie-related ones
    const allHeaders = {};
    response.headers.forEach((value, name) => {
      allHeaders[name] = value;
    });
    
    if (!response.ok) {
       
      if (response.status === 401) { 
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
     
    return data.idToken || null;
  } catch (error) {
     
    // Fallback to localStorage for development when CORS blocks backend calls
    try {
      // Get fresh tokens from Cognito
      const cognitoUser = userPool.getCurrentUser();
      
      if (cognitoUser) {
        const token = await new Promise((resolve, reject) => {
          cognitoUser.getSession((err, session) => {
          
            if (err) {
              reject(err);
              return;
            }
            if (session && session.isValid()) {
              const idToken = session.getIdToken().getJwtToken();
              resolve(idToken);
            } else {
              reject(new Error('Invalid session'));
            }
          });
        });
        return token;
      } else {
        console.error('❌ No Cognito user found');
      }
    } catch (fallbackError) {
  
    }
    
  
    return null;
  }
};

// Refresh token using backend session endpoint
const refreshAuthToken = async () => {
  try {
   
    // Get idToken directly from Cognito instead of relying on getAuthToken
    const cognitoUser = userPool.getCurrentUser();
    let currentToken = null;

   
    
    if (cognitoUser) {
      currentToken = await new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => {
          if (err) {
            reject(err);
            return;
          }
          if (session && session.isValid()) {
           
            resolve(session.getIdToken().getJwtToken());
          } else {
            reject(new Error('Invalid session'));
          }
        });
      });
    }
    
    const response = await fetch(getFullUrl('auth/session'), {
      method: 'POST',
      credentials: 'include', // Send httpOnly cookies
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        idToken: currentToken
      })
    });
   
    if (!response.ok) {
      throw new Error('Session expired, please login again');
    }

    const data = await response.json();
    return data.idToken;
  } catch (error) {
  
    throw error;
  }
};

// Make authenticated API calls with retry mechanism
export const apiCall = async (url, options = {}, retryCount = 0) => {
  const maxRetries = 1;

  try {
    const token = await getAuthToken();
   
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    // Add Authorization header if token exists
    if (token) { 
      headers['Authorization'] = `Bearer ${token}`;
    } else {
    }

    const config = {
      ...options,
      credentials: 'include', // Always include cookies for session
      headers
    };

    

    const response = await fetch(url, config);
    
    
    // Handle 401 Unauthorized - token might be expired
    if (response.status === 401 && retryCount < maxRetries) {
      
      try {
        const newToken = await refreshAuthToken();
        
        // Retry the request with new token
        const retryHeaders = {
          ...headers,
          'Authorization': `Bearer ${newToken}`
        };
        
        const retryConfig = {
          ...options,
          headers: retryHeaders
        };
        
        const retryResponse = await fetch(url, retryConfig);
        
        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText} - ${errorText}`);
        }
        
        return retryResponse;
      } catch (refreshError) {
        // Redirect to login page
         window.location.href = '/signin';
        throw new Error('Session expired, please login again');
      }
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// Public API call without authentication
export const publicApiCall = async (url, options = {}) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    const config = {
      ...options,
      headers
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
    }
    
    return response;
  } catch (error) {
    throw error;
  }
};

// Specific API methods
export const get = async (url, options = {}) => {
  return apiCall(url, { ...options, method: 'GET' });
};

// Public GET method for endpoints without authentication
export const publicGet = async (url, options = {}) => {
  return publicApiCall(url, { ...options, method: 'GET' });
};

export const post = async (url, data = {}, options = {}) => {
  return apiCall(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const put = async (url, data = {}, options = {}) => {
  return apiCall(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const del = async (url, options = {}) => {
  return apiCall(url, { ...options, method: 'DELETE' });
};
